import React, { useCallback, useState } from 'react';
import { Img } from 'react-image';
import { useRewards } from '../../Context/RewardProvider';
import Modal from '../../Modal/Modal';
import { RewardType } from '../../Types/RewardType';
import { StorylineDilemma } from '../../Types/StorylineDilemma';
import { Universe } from '../../Types/Universe';
import UniverseModal from './UniverseModal';

interface UniverseCreationCardProps {
  onUniverseCreated?: (
    universe: Universe,
    dilemmas: StorylineDilemma[],
  ) => void;
}

const UniverseCreationCard: React.FC<UniverseCreationCardProps> = ({
  onUniverseCreated,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { rewardCounts, registerRewardConsumption, isLoading } = useRewards();

  const tokenCount = rewardCounts[RewardType.UniverseCreation];
  const hasTokens = tokenCount > 0;
  const isInitialized = !isLoading;

  const handleUniverseCreated = useCallback(
    async (universe: Universe, dilemmas: StorylineDilemma[]) => {
      // Consume a universe creation token
      const success = await registerRewardConsumption(
        RewardType.UniverseCreation,
      );

      if (success) {
        onUniverseCreated?.(universe, dilemmas);
      }

      setIsSubmitting(false);
      setIsCreateModalOpen(false);
    },
    [onUniverseCreated, registerRewardConsumption],
  );

  return (
    <>
      <div
        className={`
          relative flex flex-col justify-center w-full text-sm rounded-xl max-md:mt-3 
          max-lg:mt-0
          sm:grow
          ${hasTokens ? 'cursor-pointer' : 'cursor-not-allowed'} 
          bg-gray-50 border border-gray-200
        `}
        onClick={() => hasTokens && setIsCreateModalOpen(true)}
        title={
          hasTokens
            ? undefined
            : 'Get tokens by acing post-chapter quizzes and finding loot boxes'
        }
      >
        <div
          className={`
              flex overflow-hidden relative flex-col w-full h-[180px] 
            `}
        >
          {isInitialized && (
            <div className="absolute inset-0">
              <Img
                src={[
                  hasTokens
                    ? require('../../assets/icons/universe.svg')
                    : require('../../assets/icons/universe-locked.svg'),
                ]}
                alt="Create Universe"
                className="object-cover absolute inset-0 rounded-xl m-auto mt-[32px] w-[60px]
                max-sm:mt-[22px]"
              />
            </div>
          )}
          {isInitialized && (
            <div className="absolute left-1.5 top-1.5 bg-blue-200 text-blue-800 px-2 py-1.5 rounded-full text-sm font-bold">
              Tokens: {tokenCount}
            </div>
          )}
          <div
            className="absolute flex flex-col top-0 left-0 w-full h-full text-black bg-transparent rounded-xl"
            style={{
              backgroundSize: '100% 80%',
              backgroundPosition: 'bottom',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className="px-3 pb-2 mt-auto xl:min-h-20">
              <div className="relative mb-auto text-left text-base font-medium max-md:mt-5 max-md:mr-2.5">
                Create Your Own Universe
              </div>
              <div className="relative mt-1 text-left opacity-50 max-md:mr-2.5 line-clamp-2">
                Get tokens by acing post-chapter quizzes and finding loot boxes
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        allowImplicitClose={!isSubmitting}
        maxWidthClass="max-w-lg"
      >
        <UniverseModal
          onStart={() => setIsSubmitting(true)}
          onSuccess={handleUniverseCreated}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>
    </>
  );
};

export default UniverseCreationCard;
